/**
 * Presets Store
 * Manages user presets - saved views, column selections, bookmarks.
 * 
 * Presets are fetched at app hydration and stored globally.
 * When a user changes column selection or other view settings,
 * the changes are auto-saved via the savePreset method.
 */

import { create } from 'zustand';
import { api, ApiListResponse, ApiResponse } from '@/lib/api';
import { useAuthStore, onLogout } from '@/stores/auth';
import { Preset, defaultPreset, systemDefaults } from '@/types/presets';
import { cloneDeep, merge } from 'lodash';

interface PresetsState {
    // State
    collectionPresets: Preset[];
    hydrated: boolean;
    loading: boolean;
    error: string | null;

    // Actions
    hydrate: (force?: boolean) => Promise<void>;
    dehydrate: () => void;
    create: (preset: Partial<Preset>) => Promise<Preset>;
    update: (id: number, updates: Partial<Preset>) => Promise<Preset>;
    delete: (ids: number[]) => Promise<void>;

    // Getters
    getPresetForCollection: (collection: string) => Preset;
    getBookmark: (bookmarkId: number) => Preset | null;
    savePreset: (preset: Partial<Preset>) => Promise<Preset | null>;
    saveLocal: (updatedPreset: Preset) => void;

    // Computed
    bookmarks: () => Preset[];
}

// Track current update operations to prevent race conditions
const currentUpdate: Record<number, string> = {};

export const usePresetsStore = create<PresetsState>()((set, get) => {
    // Register logout callback to dehydrate presets when user logs out
    onLogout(() => {
        set({ collectionPresets: [], hydrated: false, loading: false, error: null });
    });

    return {
        collectionPresets: [],
        hydrated: false,
        loading: false,
        error: null,

        /**
         * Hydrate the presets store by fetching all presets for the current user
         */
        hydrate: async (force = false) => {
            const { hydrated, loading } = get();

            if (!force && (hydrated || loading)) {
                return;
            }

            const { user } = useAuthStore.getState();
            if (!user) return;

            set({ loading: true, error: null });

            try {
                // Fetch user's presets
                const response = await api.get<ApiListResponse<Preset>>(
                    `/presets?filter[user][_eq]=${user.id}&limit=-1`
                );
                const userPresets = response.data || [];

                // Also fetch role presets if user has a role
                let rolePresets: Preset[] = [];
                if (user.role) {
                    const roleResponse = await api.get<ApiListResponse<Preset>>(
                        `/presets?filter[role][_eq]=${user.role}&filter[user][_null]=true&limit=-1`
                    );
                    rolePresets = roleResponse.data || [];
                }

                // Fetch global presets
                const globalResponse = await api.get<ApiListResponse<Preset>>(
                    `/presets?filter[role][_null]=true&filter[user][_null]=true&limit=-1`
                );
                const globalPresets = globalResponse.data || [];

                // Combine all presets
                const allPresets = [...userPresets, ...rolePresets, ...globalPresets];

                // Inject system defaults if they don't exist
                for (const systemCollection of Object.keys(systemDefaults)) {
                    const existingGlobalDefault = allPresets.find((preset) => {
                        return preset.collection === systemCollection &&
                            !preset.user &&
                            !preset.role &&
                            !preset.bookmark;
                    });

                    if (!existingGlobalDefault) {
                        allPresets.push(merge({}, defaultPreset, systemDefaults[systemCollection]) as Preset);
                    }
                }

                set({ collectionPresets: allPresets, hydrated: true, loading: false });
            } catch (error) {
                console.error('Failed to hydrate presets store:', error);
                set({
                    error: error instanceof Error ? error.message : 'Failed to load presets',
                    loading: false,
                });
            }
        },

        /**
         * Clear the presets store (on logout)
         */
        dehydrate: () => {
            set({ collectionPresets: [], hydrated: false, loading: false, error: null });
        },

        /**
         * Create a new preset
         */
        create: async (newPreset: Partial<Preset>) => {
            try {
                const response = await api.post<ApiResponse<Preset>>('/presets', newPreset);
                const preset = response.data;

                set((state) => ({
                    collectionPresets: [...state.collectionPresets, preset],
                }));

                return preset;
            } catch (error: any) {
                // If user doesn't have permission, silently fail and return a local-only preset
                if (error.response?.status === 403 || error.response?.status === 401) {
                    console.warn('No permission to create preset, using local-only preset');
                    // Return a temporary preset without saving to API
                    const localPreset = { ...newPreset, id: Date.now() } as Preset;
                    return localPreset;
                }
                throw error;
            }
        },

        /**
         * Update an existing preset
         */
        update: async (id: number, updates: Partial<Preset>) => {
            // Generate unique ID for this update to handle race conditions
            const updateId = Math.random().toString(36).substring(7);
            currentUpdate[id] = updateId;

            try {
                const response = await api.patch<ApiResponse<Preset>>(`/presets/${id}`, updates);
                const updatedPreset = response.data;

                // Only update state if this is still the latest update
                if (currentUpdate[id] === updateId) {
                    set((state) => ({
                        collectionPresets: state.collectionPresets.map((preset) =>
                            preset.id === updatedPreset.id ? updatedPreset : preset
                        ),
                    }));
                }

                return updatedPreset;
            } catch (error: any) {
                // If user doesn't have permission, silently fail and return the local preset
                if (error.response?.status === 403 || error.response?.status === 401) {
                    console.warn('No permission to update preset, using local-only changes');
                    // Return local version without API update
                    const localPreset = { id, ...updates } as Preset;
                    return localPreset;
                }
                throw error;
            }
        },

        /**
         * Delete presets
         */
        delete: async (ids: number[]) => {
            await api.delete('/presets', ids);

            set((state) => ({
                collectionPresets: state.collectionPresets.filter(
                    (preset) => !ids.includes(preset.id!)
                ),
            }));
        },

        /**
         * Get the most specific preset for a collection
         * Hierarchy: user > role > global
         */
        getPresetForCollection: (collection: string) => {
            const { user } = useAuthStore.getState();
            const { collectionPresets } = get();

            if (!user) {
                return {
                    ...defaultPreset,
                    collection,
                    user: null,
                } as Preset;
            }

            const defaultPresetWithCollection = {
                ...defaultPreset,
                collection,
                user: user.id,
            } as Preset;

            const availablePresets = collectionPresets.filter((preset) => {
                const userMatches = preset.user === user.id || preset.user === null;
                const roleMatches = preset.role === user.role || preset.role === null;
                const collectionMatches = preset.collection === collection;

                // Filter out bookmarks
                if (preset.bookmark) return false;

                if (userMatches && collectionMatches) return true;
                if (roleMatches && collectionMatches) return true;
                return false;
            });

            if (availablePresets.length === 0) return defaultPresetWithCollection;
            if (availablePresets.length === 1) return availablePresets[0]!;

            // In order of specificity: user > role > collection
            const userPreset = availablePresets.find((preset) => preset.user === user.id);
            if (userPreset) return userPreset;

            const rolePreset = availablePresets.find((preset) => preset.role === user.role);
            if (rolePreset) return rolePreset;

            // Return the last preset as fallback
            return availablePresets[availablePresets.length - 1]!;
        },

        /**
         * Get a bookmark by ID
         */
        getBookmark: (bookmarkId: number) => {
            const { collectionPresets } = get();
            return collectionPresets.find((preset) => preset.id === bookmarkId) || null;
        },

        /**
         * Save a preset - creates new or updates existing
         */
        savePreset: async (preset: Partial<Preset>) => {
            const { user } = useAuthStore.getState();
            if (!user) return null;

            // Clone to avoid modifying original
            preset = cloneDeep(preset);

            // Respect caller-provided scope; do not override user/role when explicitly set
            const scopeUser = preset.user ?? null;
            const scopeRole = preset.role ?? null;

            if (preset.id === undefined || preset.id === null) {
                return await get().create({
                    ...preset,
                    user: scopeUser,
                    role: scopeRole,
                });
            }

            // Update existing preset in-place (even if scope changes)
            const { id, ...updates } = preset;
            return await get().update(id!, {
                ...updates,
                user: scopeUser,
                role: scopeRole,
            });
        },

        /**
         * Save preset locally without API call (for optimistic updates)
         */
        saveLocal: (updatedPreset: Preset) => {
            set((state) => ({
                collectionPresets: state.collectionPresets.map((preset) =>
                    preset.id === updatedPreset.id ? { ...updatedPreset } : preset
                ),
            }));
        },

        /**
         * Get all bookmarks
         */
        bookmarks: () => {
            const { collectionPresets } = get();
            return collectionPresets
                .filter((preset) => preset.bookmark !== null)
                .sort((a, b) => {
                    // Global first, then role, then user
                    if (a.user === null && a.role === null) return -1;
                    if (b.user === null && b.role === null) return 1;
                    if (a.user === null && a.role !== null) return -1;
                    if (b.user === null && b.role !== null) return 1;
                    // Then alphabetically by bookmark name
                    return (a.bookmark || '').localeCompare(b.bookmark || '');
                });
        },
    };
});
